/**
 * Drop-in <img>/<video> replacements that resolve private-bucket IG media to
 * short-lived signed URLs (passthrough for raw IG CDN URLs). Swap an IG-media
 * `<img src={url} .../>` for `<SignedImg src={url} .../>` — all other props pass
 * through unchanged.
 */
import type { ImgHTMLAttributes, VideoHTMLAttributes } from 'react';
import { useSignedUrl } from '../supabase/signedMedia';

type ImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & { src?: string | null };

export function SignedImg({ src, ...rest }: ImgProps) {
  const signed = useSignedUrl(src ?? undefined);
  // While a private URL is being signed, src is undefined (no broken-image flash).
  return <img {...rest} src={signed ?? undefined} />;
}

type VideoProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'poster'> & {
  src?: string | null;
  poster?: string | null;
};

export function SignedVideo({ src, poster, ...rest }: VideoProps) {
  const signedSrc = useSignedUrl(src ?? undefined);
  const signedPoster = useSignedUrl(poster ?? undefined);
  return <video {...rest} src={signedSrc ?? undefined} poster={signedPoster ?? undefined} />;
}
